import { useState, useEffect, useMemo, memo, useCallback } from "react";
import {
  TextField,
  Grid,
  Button,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  Box,
} from "@mui/material";
import { useDictionaries } from "../hooks/useDictionaries";
import { useAsyncAutocomplete } from "../hooks/useAsyncAutocomplete";
import { usePaidFilters } from "../hooks/usePaidFilters";
import { getCustomers, getOked } from "../api/dictionaries";

const SearchFilter = ({ filters, setFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const dictKeys = useMemo(() => ["LotTrade", "Region", "LotSubjectType"], []);
  const { data: dictionaries, isLoading: isDictLoading } =
    useDictionaries(dictKeys);
  const { paidFilters, isLoading: paidLoading } = usePaidFilters();

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleLocalChange = useCallback((field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      ...localFilters,
      page: 1,
    }));
  };

  const toISOString = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString();
  };

  const {
    inputValue: customerText,
    setInputValue: setCustomerText,
    options: customerOptions,
    loading: customerLoading,
  } = useAsyncAutocomplete(getCustomers);

  const {
    inputValue: okedText,
    setInputValue: setOkedText,
    options: okedOptions,
    loading: okedLoading,
  } = useAsyncAutocomplete(getOked);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* === Базовые фильтры === */}
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Наименование лота"
          value={localFilters.text ?? ""}
          onChange={(e) => handleLocalChange("text", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Номер лота"
          value={localFilters.number ?? ""}
          onChange={(e) => handleLocalChange("number", e.target.value)}
        />
      </Grid>

      {/* === Дополнительные фильтры === */}
      {showAdvanced && (
        <>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={dictionaries?.LotTrade || []}
              getOptionLabel={(option) => option.name || ""}
              value={localFilters.trades}
              onChange={(_, value) => handleLocalChange("trades", value)}
              multiple
              renderInput={(params) => (
                <TextField {...params} label="Вид торговли" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={dictionaries?.Region || []}
              getOptionLabel={(option) => option.name || ""}
              value={localFilters.regionCodes}
              onChange={(_, value) => handleLocalChange("regionCodes", value)}
              multiple
              renderInput={(params) => <TextField {...params} label="Регион" />}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={dictionaries?.LotSubjectType || []}
              getOptionLabel={(option) => option.name || ""}
              value={localFilters.subjectTypes}
              onChange={(_, value) => handleLocalChange("subjectTypes", value)}
              multiple
              renderInput={(params) => (
                <TextField {...params} label="Тип предмета лота" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              multiple
              options={paidFilters}
              value={localFilters.padiFilterIds || []}
              getOptionLabel={(o) =>
                o.parentName ? `${o.parentName} → ${o.name}` : o.name
              }
              onChange={(_, value) => handleLocalChange("padiFilterIds", value)}
              renderInput={(params) => (
                <TextField {...params} label="Платные фильтры" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={customerOptions}
              getOptionLabel={(option) => option.name || ""}
              value={localFilters.customerIdentifier}
              onChange={(_, value) =>
                handleLocalChange("customerIdentifier", value)
              }
              inputValue={customerText}
              onInputChange={(_, v) => setCustomerText(v)}
              loading={customerLoading}
              renderInput={(params) => (
                <TextField {...params} label="Заказчик" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={okedOptions}
              getOptionLabel={(option) => option.name || ""}
              value={localFilters.oked}
              onChange={(_, value) => handleLocalChange("oked", value)}
              inputValue={okedText}
              onInputChange={(_, v) => setOkedText(v)}
              loading={okedLoading}
              renderInput={(params) => <TextField {...params} label="ОКЭД" />}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата начала от"
              InputLabelProps={{ shrink: true }}
              value={localFilters.dateStartFrom || ""}
              onChange={(e) =>
                handleLocalChange("dateStartFrom", toISOString(e.target.value))
              }
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата начала до"
              InputLabelProps={{ shrink: true }}
              value={localFilters.dateStartTo || ""}
              onChange={(e) =>
                handleLocalChange("dateStartTo", toISOString(e.target.value))
              }
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата окончания от"
              InputLabelProps={{ shrink: true }}
              value={localFilters.dateEndFrom || ""}
              onChange={(e) =>
                handleLocalChange("dateEndFrom", toISOString(e.target.value))
              }
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата окончания до"
              InputLabelProps={{ shrink: true }}
              value={localFilters.dateEndTo || ""}
              onChange={(e) =>
                handleLocalChange("dateEndTo", toISOString(e.target.value))
              }
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              type="number"
              label="Цена за ед. от"
              value={localFilters.unitPriceFrom || ""}
              onChange={(e) =>
                handleLocalChange("unitPriceFrom", Number(e.target.value) || "")
              }
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              type="number"
              label="Цена за ед. до"
              value={localFilters.unitPriceTo || ""}
              onChange={(e) =>
                handleLocalChange("unitPriceTo", Number(e.target.value) || "")
              }
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              type="number"
              label="Сумма от"
              value={localFilters.amountFrom || ""}
              onChange={(e) =>
                handleLocalChange("amountFrom", Number(e.target.value) || "")
              }
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              type="number"
              label="Сумма до"
              value={localFilters.amountTo || ""}
              onChange={(e) =>
                handleLocalChange("amountTo", Number(e.target.value) || "")
              }
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={2}>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced
            ? "Скрыть дополнительные фильтры"
            : "Дополнительные фильтры"}
        </Button>
      </Grid>
      {/* === Поиск === */}
      <Grid item xs={12} sm={2}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSearch}
        >
          Поиск
        </Button>
      </Grid>
    </Grid>
  );
};

export default memo(SearchFilter);
